import { Component } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CommonModule } from '@angular/common';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CurrencyExchangeService } from '../../../../core/services/currency-exchange.service';
import { concat, toArray } from 'rxjs';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CalendarModule,
    InputTextModule,
    ButtonModule,
    ToastModule,
    TranslatePipe
  ],
  templateUrl: './add-currency-form.component.html',
  providers: [MessageService]
})
export class AddRateExchangeComponent {
  errorMessages: any = {
    pattern: {
      currency: 'layout.configurations.currencies.addCurrency.currencyException',
      exchangeRate: 'layout.configurations.currencies.addCurrency.exchangeRateException',
    },
    min: {
      exchangeRate: 'layout.configurations.currencies.addCurrency.exchangeRateMin',
    },
  };
  isEditMode = false;
  form!: FormGroup;
  translations: any = {};

  constructor(
    private fb: FormBuilder,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private translate: TranslateService,
    private currencyExchangeService: CurrencyExchangeService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.isEditMode = this.config.data?.mode === 'edit';
    this.loadTranslations();
    const currencies: string[] = this.config.data.currencies || [];
    const existingRates = this.config.data?.rates || [];

    this.form = this.fb.group({
      startDate: [this.config.data?.startDate || null, Validators.required],
      endDate: [this.config.data?.endDate || null],
      rates: this.fb.array(
        currencies.map(c => {
          const existing = existingRates.find((r: any) => r.currency === c);
          return this.fb.group({
            currency: [c],
            exchangeRate: [existing?.exchangeRate ?? null, [Validators.min(0.01)]]
          });
        })
      )
    }, { validators: this.dateRangeValidator });
  }

  dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    const startDate = control.get('startDate')?.value;
    const endDate = control.get('endDate')?.value;
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      return { dateRange: true };
    }
    return null;
  }

  get rates(): FormArray {
    return this.form.get('rates') as FormArray;
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.currencies.addCurrency.errorMessage.title',
        'layout.configurations.currencies.addCurrency.errorMessage.alreadyExists',
        'layout.configurations.currencies.addCurrency.errorMessage.errorSavingCurrency',
        'layout.configurations.currencies.addCurrency.successMessage.title',
        'layout.configurations.currencies.addCurrency.successMessage.detail',
        'layout.configurations.currencies.editCurrency.errorMessage.title',
        'layout.configurations.currencies.editCurrency.errorMessage.alreadyExists',
        'layout.configurations.currencies.editCurrency.errorMessage.errorUpdatingCurrency',
        'layout.configurations.currencies.editCurrency.successMessage.title',
        'layout.configurations.currencies.editCurrency.successMessage.detail',
        'layout.configurations.currencies.addrateexchange.successMessage.title',
        'layout.configurations.currencies.addrateexchange.successMessage.detail'
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }
  isInvalid(key: string) {
    const control = this.form.get(key);
    if (!control || !(control.dirty || control.touched) || !control.errors) return { invalid: false };
    if (control.errors['required']) return { invalid: true, message: this.errorMessages.required?.[key] };
    if (control.errors['pattern']) return { invalid: true, message: this.errorMessages.pattern?.[key] };
    if (control.errors['min']) return { invalid: true, message: this.errorMessages.min?.[key] };
    return { invalid: true, message: 'layout.configurations.currencies.addrateexchange.invalidField' };
  }

  get dateRangeError(): string {
    if (!this.form.errors?.['dateRange'] || !(this.form.dirty || this.form.touched)) {
      return '';
    }
    return 'layout.configurations.currencies.addrateexchange.dateRange';
  }

  isRateInvalid(index: number): { invalid: boolean; message?: string } {
    const control = this.rates.at(index).get('exchangeRate');
    if (!control || !(control.dirty || control.touched) || !control.errors) return { invalid: false };
    if (control.errors['min']) return { invalid: true, message: this.errorMessages.min?.exchangeRate };
    return { invalid: true, message: 'layout.configurations.currencies.addrateexchange.invalidField' };
  }

  private formatDateLocal(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  saveRate() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isEditMode) {
      this.ref.close({
        startDate: this.form.value.startDate,
        endDate: this.form.value.endDate,
        rates: this.form.value.rates
      });
      return;
    }

    // ADD MODE: send each rate to backend
    const startDate: Date = this.form.value.startDate;
    const formattedStartDate = this.formatDateLocal(startDate);
    const endDate: Date | null = this.form.value.endDate;
    const formattedEndDate = endDate ? this.formatDateLocal(endDate) : null;

    const rates = this.form.value.rates.filter(
      (r: any) => r.exchangeRate !== null && r.exchangeRate !== undefined && r.exchangeRate !== ''
    );

    if (rates.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translations['layout.configurations.currencies.addCurrency.errorMessage.title'],
        detail: this.translate.instant('layout.configurations.currencies.addrateexchange.atLeastOneRate'),
        life: 4000
      });
      return;
    }
    const calls = rates.map((r: any) =>
      this.currencyExchangeService.addRateExchange({
        currencyName: r.currency,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        exchangeRate: Number(r.exchangeRate),
      })
    );

    concat(...calls).pipe(toArray()).subscribe({
      next: () => {
        this.ref.close({
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          rates: this.form.value.rates
        });
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.currencies.addCurrency.errorMessage.title'],
          detail: err?.error?.message || this.translations['layout.configurations.currencies.addCurrency.errorMessage.errorSavingCurrency'],
          life: 5000
        });
      }
    });
  }

  onCancel() {
    this.ref.close();
  }
}
